// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The game, as the display sees it (M3-17).
//
// `DmdPlayer` renders scenes from a `DmdScoreView` and deliberately never reaches into the
// game to get one — the comment on that interface reads "supplied by the game, never read
// from it", and this is the supplying. Keeping the direction of that dependency is what lets
// the display be driven by a replay, by a test, or by an attract loop with nothing behind it.
//
// It also means this adapter is the only place that can get the mapping wrong, and a wrong
// mapping is quiet: a display showing player 1's score during player 2's ball looks exactly
// like a display working.

import type { Game } from '@core/game'
import type { MachineDefinition } from '@core/machine'
import { DEFAULT_START_HINT, DmdPlayer, type DmdRaster, type DmdScoreView } from './dmd'

/**
 * What the machine's display should be showing about the game right now.
 *
 * `currentPlayer` is zero-based here and one-based on the glass. The conversion belongs to
 * whichever scene chooses to print it, because `scores` is indexed by the same number —
 * converting here would break the indexing in order to fix the printing.
 */
export function scoreView(game: Game, title?: string): DmdScoreView {
  // **The mode is read from the engine rather than tracked here.** A second copy of "which mode is
  // running" is a second thing that can be wrong, and this one would be wrong in the direction
  // nobody notices: a display that keeps naming a mode that ended.
  const mode = game.engine.runningMode()
  return {
    variables: Object.fromEntries((game.definition.rules?.vars ?? []).filter(v => v.type !== 'set').map(v => [v.id, game.engine.vars.get(v.id, game.currentPlayer)])),
    scores: game.players.map((p) => p.score),
    currentPlayer: game.currentPlayer,
    ball: game.currentBall,
    // Read from the game for the same reason the mode is: a second copy of the credit count is a
    // second thing that can disagree with the coin slot.
    credits: game.credits,
    // **What the machine is asking for, so the glass can ask it** (M8-47). Read from the game for
    // the same reason the mode and the credits are: a second copy of "which letter is being
    // edited" is a second thing that can disagree with the flippers that are turning it.
    //
    // Gated on the phase rather than on `initialsFor >= 0`, because that is the test
    // `machineRuntime.ts` already uses for this client's HUD, and the glass and the HUD
    // disagreeing about whether the machine is asking would be worse than either being wrong.
    // `initialsFor` outlives the asking by design — `PlaySession` reads it to tell a finished
    // game from one still taking initials.
    initials: game.phase === 'highScoreEntry' ? game.initialsText : '',
    initialsAt: game.phase === 'highScoreEntry' ? game.initialsPosition : -1,
    initialsFor: game.phase === 'highScoreEntry' ? game.initialsFor : -1,
    ...(title !== undefined ? { title } : {}),
    ...(mode === undefined ? {} : { mode: { name: mode.title, remaining: mode.remaining } }),
  }
}

/**
 * Scenes that suit a game phase, most specific first.
 *
 * A list rather than a name, because a machine is not obliged to define any of them. NEON
 * NOVA has an `attract` and a `bonusCount` but no `gameOver`; another machine will have the
 * opposite. Ending every list with `scoreMain` means the fallback is a display showing the
 * score, which is never wrong, rather than a display showing whatever it was showing before —
 * which is how a machine ends up stuck on a jackpot animation for the rest of the game.
 */
export function idleScenesFor(phase: Game['phase']): readonly string[] {
  switch (phase) {
    case 'attract':
      return ['attract', 'scoreMain']
    case 'gameOver':
      return ['gameOver', 'match', 'attract', 'scoreMain']
    case 'highScoreEntry':
      return ['highScoreEntry', 'scoreMain']
    case 'betweenBalls':
      return ['ballOver', 'scoreMain']
    case 'bonus':
      return ['bonusCount', 'scoreMain']
    case 'ballInPlay':
      return ['scoreMain']
  }
}

/**
 * Drives one machine's display from one game.
 *
 * Two jobs, and they are separate on purpose. A **rule** asks for a scene — a jackpot, a mode
 * intro — and that is a one-off with a duration, after which the player hands back to whatever
 * is idle. The **phase** decides what idle *means*, and it changes without any rule firing at
 * all: a ball drains, and the display should stop showing the score and start counting bonus,
 * because that is what the machine is doing.
 *
 * Without the second job the display is correct for as long as rules keep firing and then
 * freezes on the last thing that happened, which is a display that looks alive during a good
 * ball and dead during a bad one.
 */
export class DisplayDriver {
  readonly player: DmdPlayer
  private lastPhase: Game['phase'] | undefined

  /**
   * @param startHint How this host says "launch the ball" (see `DmdRenderContext.startHint`).
   *   Passed in rather than measured here, because `src/fx` draws and does not know what kind of
   *   device it is drawing on — that is the client's question, and `startHintFor` answers it.
   */
  constructor(definition: MachineDefinition, startHint: string = DEFAULT_START_HINT) {
    this.player = new DmdPlayer(definition.display)
    // Default scene text must remain legible over inverted/filled bright backgrounds.
    // This changes neither machine artwork bytes nor explicitly authored intensities.
    this.player.contrastText = true
    this.player.startHint = startHint
  }

  /** Whether this machine has a display at all. A machine may legitimately have none. */
  get present(): boolean {
    return this.player.raster.width > 0 && this.player.has(this.player.idleScene)
  }

  /**
   * A rule asked for a scene.
   *
   * Returns whether the machine has it. A missing scene leaves the display alone rather than
   * blanking it — a rule naming a scene the machine never defined is an authoring mistake, and
   * a blank display is a worse way to report it than a display that carries on.
   */
  request(sceneId: string, step: number): boolean {
    return this.player.show(sceneId, step)
  }

  /**
   * A new game has begun at step 0 on the same session.
   *
   * Scenes are timed in steps from the step they were shown at, so a scene shown late in the
   * previous game would otherwise wait that many steps — minutes — before handing back to the
   * score. The idle scene is shown again from step 0, and the phase memory is dropped so the
   * first render of the new game picks the idle scene its phase wants.
   */
  restart(): void {
    this.lastPhase = undefined
    this.player.show(this.player.idleScene, 0)
  }

  /**
   * Keep the idle scene in step with the phase, then draw.
   *
   * The switch happens only when the phase actually changes, so a timed scene a rule started
   * plays out instead of being stamped on once a frame.
   */
  render(game: Game, step: number): DmdRaster {
    if (game.phase !== this.lastPhase) {
      const wasIdle = this.player.sceneId === undefined || this.player.sceneId === this.player.idleScene
      for (const candidate of idleScenesFor(game.phase)) {
        if (!this.player.has(candidate)) continue
        this.player.idleScene = candidate
        break
      }
      this.lastPhase = game.phase
      if (wasIdle) this.player.show(this.player.idleScene, step)
    }
    return this.player.render(step, scoreView(game, game.definition.meta.title))
  }
}
